import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Plus, Pencil, Trash2, UserCog, Shield } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERMISSIONS = [
  "products.read", "products.write", "products.delete",
  "orders.read", "orders.write",
  "inventory.read", "inventory.write",
  "users.read", "users.write",
  "drivers.read", "drivers.write",
  "reports.read",
];

const AdminUsers = () => {
  const { user: currentUser, getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    username: "", password: "", role: "staff", full_name: "", phone: "", email: "", permissions: [],
  });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: getAuthHeaders() });
      setUsers(res.data.filter((u) => u.role !== "customer"));
    } catch (error) {
      toast.error(t("failedToFetchUsers"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      if (editing) {
        const { password, username, ...updateData } = formData;
        await axios.put(`${API}/users/${editing.id}`, updateData, { headers });
        toast.success(t("userUpdated"));
      } else {
        await axios.post(`${API}/users`, formData, { headers });
        toast.success(t("userCreated"));
      }
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("failedToSaveUser"));
    }
  };

  const handleDelete = async (id) => {
    if (id === currentUser?.id) {
      toast.error(t("cannotDeleteOwnAccount"));
      return;
    }
    if (!window.confirm(t("deleteUserConfirm"))) return;
    try {
      await axios.delete(`${API}/users/${id}`, { headers: getAuthHeaders() });
      toast.success(t("userDeleted"));
      fetchUsers();
    } catch (error) {
      toast.error(t("failedToDeleteUser"));
    }
  };

  const openEdit = (user) => {
    setEditing(user);
    setFormData({
      username: user.username,
      password: "",
      role: user.role,
      full_name: user.full_name || "",
      phone: user.phone || "",
      email: user.email || "",
      permissions: user.permissions || [],
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({ username: "", password: "", role: "staff", full_name: "", phone: "", email: "", permissions: [] });
  };

  const togglePermission = (perm) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter((p) => p !== perm)
        : [...prev.permissions, perm],
    }));
  };

  const getRoleColor = (role) => {
    const colors = { admin: "bg-[#d4af37]/20 text-[#d4af37]", staff: "bg-blue-500/20 text-blue-400", driver: "bg-purple-500/20 text-purple-400" };
    return colors[role] || "bg-slate-500/20 text-slate-400";
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div className="flex justify-between">
        <h2 className="text-xl font-heading font-semibold text-white">{t("userManagement")} ({users.length})</h2>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#d4af37] text-[#0f172a]" data-testid="add-user-btn">
              <Plus className="mr-2 h-4 w-4" /> {t("addUser")}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">{editing ? t("editUser") : t("addUser")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">{t("username")} *</Label>
                  <Input
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required={!editing}
                    disabled={!!editing}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                {!editing && (
                  <div>
                    <Label className="text-slate-300">{t("password")} *</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="bg-slate-700 border-slate-600"
                    />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">{t("fullName")}</Label>
                  <Input value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} className="bg-slate-700 border-slate-600" />
                </div>
                <div>
                  <Label className="text-slate-300">{t("role")} *</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                    <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="admin">{t("admin")}</SelectItem>
                      <SelectItem value="staff">{t("staff")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">{t("email")}</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-slate-700 border-slate-600" />
                </div>
                <div>
                  <Label className="text-slate-300">{t("phone")}</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="bg-slate-700 border-slate-600" />
                </div>
              </div>
              
              {formData.role === "staff" && (
                <div>
                  <Label className="text-slate-300 mb-2 block">{t("permissions")}</Label>
                  <div className="grid grid-cols-2 gap-2 p-4 bg-slate-700/50 rounded-lg">
                    {PERMISSIONS.map((perm) => (
                      <label key={perm} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.permissions.includes(perm)}
                          onChange={() => togglePermission(perm)}
                          className="rounded border-slate-600"
                        />
                        <span className="text-slate-300">{perm}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600">{t("cancel")}</Button>
                <Button type="submit" className="bg-[#d4af37] text-[#0f172a]" data-testid="save-user-btn">{editing ? t("update") : t("create")}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-400">{t("loading")}</div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center">
              <UserCog className="w-12 h-12 mx-auto text-slate-500 mb-4" />
              <p className="text-slate-400">{t("noUsers")}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-300">{t("user")}</TableHead>
                  <TableHead className="text-slate-300">{t("role")}</TableHead>
                  <TableHead className="text-slate-300">{t("contact")}</TableHead>
                  <TableHead className="text-slate-300">{t("permissions")}</TableHead>
                  <TableHead className="text-slate-300">{t("status")}</TableHead>
                  <TableHead className="text-slate-300 text-right">{t("actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="border-slate-700">
                    <TableCell>
                      <p className="text-white font-medium">{user.full_name || user.username}</p>
                      <p className="text-xs text-slate-400">@{user.username}</p>
                    </TableCell>
                    <TableCell><Badge className={getRoleColor(user.role)}>{user.role}</Badge></TableCell>
                    <TableCell>
                      <p className="text-slate-300 text-sm">{user.email || "-"}</p>
                      <p className="text-slate-400 text-xs">{user.phone || "-"}</p>
                    </TableCell>
                    <TableCell>
                      {user.role === "admin" ? (
                        <Badge className="bg-[#d4af37]/20 text-[#d4af37]"><Shield className="w-3 h-3 mr-1" />{t("fullAccess")}</Badge>
                      ) : (
                        <span className="text-slate-400 text-sm">{user.permissions?.length || 0} {t("permissions")}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={user.is_active ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                        {user.is_active !== false ? t("active") : t("inactive")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(user)} className="text-slate-400 hover:text-white">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {user.id !== currentUser?.id && (
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(user.id)} className="text-red-400 hover:text-red-300">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminUsers;
